import {
  connectionDevelopersId,
  connectionWordpressPluginId,
  ipgHelpId,
  ipgTechDocId,
  reportPaymentId,
  solutionInvoiceId,
  solutionProductsId,
} from "@/assets/googleTrackIds";
import { Link } from "react-router-dom";
import qs from "query-string";
import { FiCpu, FiGrid, FiPieChart, FiRadio } from "@payping/icons";
import { Card, Col, Flex, Grid, Row, Skeleton, Typography } from "@payping/web-components";
import { useAppSelector, useAppTranslation } from "@/utils";
import { DashboardRoutes } from "@/router";
import style from "./style.module.scss";
import { URLS } from "@payping/utilities-shared/consts";

interface Props {
  withdrawListLoading: boolean;
}

const LinksBoard = ({ withdrawListLoading }: Props) => {
  const { t } = useAppTranslation();
  const hasWithdraw = useAppSelector((state) => state.user.overview.hasWithdraw);
  const { lg } = Grid.useBreakpoint();

  if (withdrawListLoading) {
    return <Skeleton active paragraph />;
  }
  if (hasWithdraw) return null;

  const links = {
    payment: [
      <Link key={1} to={URLS.DOCS_URL} data-google-track={ipgTechDocId}>
        {t("overview.linksBoard.payment.links.documents")}
      </Link>,
      <a
        key={2}
        target={"_blank"}
        href={`${URLS.HELP_URL}fa/prdakht-sadh-drgah-prdakht-aintrnti/`}
        rel={"noreferrer"}
        data-google-track={ipgHelpId}
      >
        {t("overview.linksBoard.payment.links.paymentGateway")}
      </a>,
    ],
    reports: [
      <Link key={1} to={DashboardRoutes.mySale.report.url} data-google-track={reportPaymentId}>
        {t("overview.linksBoard.report.links.paymentsReport")}
      </Link>,
    ],
    connections: [
      <Link
        key={1}
        to={{
          pathname: DashboardRoutes.connections.plugins.root.url,
          search: qs.stringify({ searchList: "wordpress" }),
        }}
        data-google-track={connectionWordpressPluginId}
      >
        {t("overview.linksBoard.connections.links.wordpress")}
      </Link>,
      <Link key={2} to={DashboardRoutes.connections.developers.root.url} data-google-track={connectionDevelopersId}>
        {t("overview.linksBoard.connections.links.developers")}
      </Link>,
    ],
    solutions: [
      <Link key={1} to={DashboardRoutes.myShop.invoice.list.url} data-google-track={solutionInvoiceId}>
        {t("overview.linksBoard.solutions.links.onlineInvoice")}
      </Link>,
      <Link key={2} to={DashboardRoutes.myShop.product.list.url} data-google-track={solutionProductsId}>
        {t("overview.linksBoard.solutions.links.products")}
      </Link>,
    ],
  } as const;

  return (
    <Card.Ant className="tw-w-full">
      <Row gutter={[16, 16]}>
        {/* < درگاه پرداخت اینترنتی > */}
        <Col xs={24} xl={12}>
          <Flex className="tw-w-full">
            {/* < تایتل و متن و لینک > */}
            <Flex vertical gap="middle" className="tw-w-full">
              <Typography.Title level={4} className="!tw-m-0">
                {t("overview.linksBoard.payment.title")}
              </Typography.Title>
              <Typography.Text className="color--gray500">{t("overview.linksBoard.payment.content")}</Typography.Text>
              {lg ? (
                <Flex gap="middle" wrap="wrap">
                  {links.payment}
                </Flex>
              ) : null}
            </Flex>
            {/* < تایتل و متن و لینک /> */}

            {/* < ایکن و دیزاین مربوطه > */}
            <Flex justify="center" align="start" className="tw-relative">
              <div className={style.paymentDottedOne}></div>
              <div className={style.paymentDottedTwo}></div>
              <div className={style.paymentDottedThree}></div>
              <div className="tw-p-8">
                <FiCpu width={"32px"} className="tw-z-[4]" />
              </div>
            </Flex>
            {/* < ایکن و دیزاین مربوطه /> */}
          </Flex>
          {lg ? null : (
            <Flex gap="middle" wrap="wrap">
              {links.payment}
            </Flex>
          )}
        </Col>
        {/* < درگاه پرداخت اینترنتی /> */}

        {/* < گزارشات > */}
        <Col xs={24} xl={12}>
          <Flex className="tw-w-full" justify={"space-between"}>
            <Flex vertical gap="middle" className="tw-w-full">
              <Typography.Title level={4} className="!tw-m-0">
                {t("overview.linksBoard.report.title")}
              </Typography.Title>
              {/*  بخاطر جایه دیزاین عکس و نقطه ها دستی استایل دادم که درست نشون داده بشه تو موبایل  */}
              <Typography.Text className="color--gray500">{t("overview.linksBoard.report.content")}</Typography.Text>
              {lg ? (
                <Flex gap="middle" wrap="wrap">
                  {links.reports}
                </Flex>
              ) : null}
            </Flex>
            <Flex justify="center" align="start" className="tw-relative">
              <div className={style.reportDottedOne}></div>
              <div className={style.reportDottedTwo}></div>
              <div className={style.reportDottedThree}></div>
              <div className="tw-p-8">
                <FiPieChart width={"32px"} className="tw-z-[4]" />
              </div>
            </Flex>
          </Flex>
          {lg ? null : (
            <Flex gap={"middle"} wrap="wrap">
              {links.reports}
            </Flex>
          )}
        </Col>
        {/* < گزارشات /> */}

        {/* < اتصالات > */}
        <Col xs={24} xl={12}>
          <Flex className="tw-w-full">
            <Flex vertical gap="middle" className="tw-w-full">
              <Typography.Title level={4} className="!tw-m-0">
                {t("overview.linksBoard.connections.title")}
              </Typography.Title>
              <Typography.Text className="color--gray500">
                {t("overview.linksBoard.connections.content")}
              </Typography.Text>
              {lg ? (
                <Flex gap="middle" wrap="wrap">
                  {links.connections}
                </Flex>
              ) : null}
            </Flex>
            <Flex justify="center" align="start" className="tw-relative">
              <div className={style.connectionDottedOne}></div>
              <div className={style.connectionDottedTwo}></div>
              <div className={style.connectionDottedThree}></div>
              <div className="tw-p-8">
                <FiRadio width={"32px"} className="tw-z-[4]" />
              </div>
            </Flex>
          </Flex>
          {lg ? null : (
            <Flex gap="middle" wrap="wrap">
              {links.connections}
            </Flex>
          )}
        </Col>
        {/* < اتصالات /> */}

        {/* < راه کارها > */}
        <Col xs={24} xl={12}>
          <Flex className="tw-w-full">
            <Flex vertical gap="middle" className="tw-w-full">
              <Typography.Title level={4} className="!tw-m-0">
                {t("overview.linksBoard.solutions.title")}
              </Typography.Title>
              <Typography.Text className="color--gray500">{t("overview.linksBoard.solutions.content")}</Typography.Text>
              {lg ? <Flex gap="small">{links.solutions}</Flex> : null}
            </Flex>
            <Flex justify="center" align="start" className="tw-relative">
              <div className={style.solutionDottedOne}></div>
              <div className={style.solutionDottedTwo}></div>
              <div className={style.solutionDottedThree}></div>
              <div className="tw-p-8">
                <FiGrid width={"32px"} className="tw-z-[4]" />
              </div>
            </Flex>
          </Flex>
          {lg ? null : (
            <Flex gap="middle" wrap="wrap">
              {links.solutions}
            </Flex>
          )}
        </Col>
        {/* < راه کارها /> */}
      </Row>
    </Card.Ant>
  );
};

export default LinksBoard;
